/**
 * Stato conservazione MARTEWEB di un documento archiviato (registro / xFIR) e download DIP.
 * "Inviato" = file caricato su MARTEWEB; "Conservato" = confermato dall'API MarteWeb.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FileArchive } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage, endpoints, type ArchiveDocumentRow } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS_STALE_MS = 5 * 60 * 1000;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("it-IT");
}

export function ConservazioneStato({
  doc,
  compact = false,
}: {
  doc: ArchiveDocumentRow;
  compact?: boolean;
}) {
  const [downloading, setDownloading] = useState(false);
  const checkable =
    doc.status === "archived" &&
    (doc.conservation_status == null ||
      doc.conservation_status === "sent" ||
      doc.conservation_status === "archived");

  const statusQ = useQuery({
    queryKey: ["archive", "conservation", doc.id],
    queryFn: () => endpoints.archive.conservationStatus(doc.id),
    enabled: checkable,
    staleTime: STATUS_STALE_MS,
    retry: false,
  });

  const row = statusQ.data ?? doc;
  const cls = compact ? "text-[10px] px-1 py-0" : "text-xs font-normal";

  const downloadDip = async () => {
    setDownloading(true);
    try {
      await endpoints.archive.downloadDip(doc.id);
    } catch (e) {
      toast.error("Download DIP non riuscito", { description: apiErrorMessage(e) });
    } finally {
      setDownloading(false);
    }
  };

  let badge: React.ReactNode = null;
  switch (row.conservation_status) {
    case "archived":
      badge = (
        <Badge variant="success" className={cls}>
          Conservato {formatDate(row.conservation_archived_at)}
        </Badge>
      );
      break;
    case "sent":
      badge = (
        <Badge
          variant="outline"
          className={cls}
          title="Inviato a MARTEWEB, in attesa della conferma di conservazione"
        >
          Inviato in conservazione {formatDate(row.conservation_sent_at)}
        </Badge>
      );
      break;
    case "failed":
    case "skipped":
    case "non_rentri":
      badge = (
        <Badge variant="destructive" className={cls} title={row.conservation_error ?? undefined}>
          Errore conservazione
        </Badge>
      );
      break;
    default:
      if (doc.status === "archived") {
        badge = (
          <Badge variant="outline" className={cls}>
            Invio in conservazione…
          </Badge>
        );
      }
  }

  const dipAvailable = !!statusQ.data?.dip_available;

  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      {dipAvailable && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={compact ? "h-6 px-2" : undefined}
          disabled={downloading}
          onClick={downloadDip}
        >
          <FileArchive className="mr-1 h-3.5 w-3.5" />
          {downloading ? "Download…" : "Scarica DIP"}
        </Button>
      )}
    </span>
  );
}
